import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { FileTextIcon, SearchIcon, FilterIcon, ChevronDownIcon, CheckCircleIcon, XCircleIcon, EyeIcon, UserIcon, GraduationCapIcon, CalendarIcon, AlertCircleIcon, ClockIcon, MessageSquareIcon, DownloadIcon } from 'lucide-react';
const MoHESRApplications: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [trackFilter, setTrackFilter] = useState('All Tracks');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [priorityFilter, setPriorityFilter] = useState('All Priorities');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Sample applications data
  const applications = [{
    id: 'app001',
    studentName: 'Ahmed Al Mansouri',
    studentId: 'STU12345',
    track: 'Elite',
    program: 'Computer Science at MIT',
    university: 'Massachusetts Institute of Technology',
    employer: null,
    status: 'final_review',
    priority: 'High',
    submissionDate: '2023-06-15',
    lastUpdated: '2023-06-20',
    gpa: 3.9,
    englishScore: 'IELTS 7.5',
    additionalTests: 'SAT 1580',
    workExperience: '2 years software development',
    recommendation: 'Accept',
    reviewNotes: 'Exceptional candidate with strong technical background and excellent academic record.',
    documents: ['Transcript', 'CV', 'Personal Statement', 'Recommendation Letters (2)', 'English Test', 'SAT Scores'],
    interviewScore: 95,
    employerRecommendation: null
  }, {
    id: 'app002',
    studentName: 'Fatima Al Zaabi',
    studentId: 'STU12346',
    track: 'Partnership',
    program: 'Petroleum Engineering',
    university: 'Stanford University',
    employer: 'ADNOC',
    status: 'final_review',
    priority: 'High',
    submissionDate: '2023-06-12',
    lastUpdated: '2023-06-18',
    gpa: 3.7,
    englishScore: 'IELTS 6.5',
    additionalTests: null,
    workExperience: 'Internship at ADNOC',
    recommendation: 'Accept',
    reviewNotes: 'Strong candidate with clear career alignment. ADNOC highly recommends.',
    documents: ['Transcript', 'CV', 'Personal Statement', 'Recommendation Letters (2)', 'English Test'],
    interviewScore: 88,
    employerRecommendation: 'Highly Recommended'
  }, {
    id: 'app003',
    studentName: 'Mohammed Al Hashimi',
    studentId: 'STU12347',
    track: 'Elite',
    program: 'Medicine at Harvard',
    university: 'Harvard University',
    employer: null,
    status: 'pending_decision',
    priority: 'Critical',
    submissionDate: '2023-06-10',
    lastUpdated: '2023-06-19',
    gpa: 3.95,
    englishScore: 'IELTS 8.0',
    additionalTests: 'MCAT 520',
    workExperience: 'Research assistant at UAEU',
    recommendation: 'Accept',
    reviewNotes: 'Outstanding medical school candidate with research experience and top test scores.',
    documents: ['Transcript', 'CV', 'Personal Statement', 'Recommendation Letters (3)', 'English Test', 'MCAT Scores', 'Research Portfolio'],
    interviewScore: 97,
    employerRecommendation: null
  }, {
    id: 'app004',
    studentName: 'Sara Al Dhaheri',
    studentId: 'STU12348',
    track: 'Partnership',
    program: 'Digital Innovation',
    university: 'Multiple Options',
    employer: 'Etisalat',
    status: 'under_review',
    priority: 'Medium',
    submissionDate: '2023-06-08',
    lastUpdated: '2023-06-16',
    gpa: 3.6,
    englishScore: 'IELTS 7.0',
    additionalTests: 'Programming Assessment: Excellent',
    workExperience: '1 year at tech startup',
    recommendation: 'Pending',
    reviewNotes: 'Good technical skills, awaiting final employer assessment.',
    documents: ['Transcript', 'CV', 'Personal Statement', 'Recommendation Letters (2)', 'English Test', 'Portfolio'],
    interviewScore: 82,
    employerRecommendation: 'Under Review'
  }, {
    id: 'app005',
    studentName: 'Khalid Al Mazrouei',
    studentId: 'STU12349',
    track: 'Elite',
    program: 'Business at London Business School',
    university: 'London Business School',
    employer: null,
    status: 'rejected',
    priority: 'Low',
    submissionDate: '2023-06-05',
    lastUpdated: '2023-06-17',
    gpa: 3.2,
    englishScore: 'IELTS 6.0',
    additionalTests: 'GMAT 650',
    workExperience: '3 years banking',
    recommendation: 'Reject',
    reviewNotes: 'Does not meet minimum GPA requirement for Elite Track. Consider alternative programs.',
    documents: ['Transcript', 'CV', 'Personal Statement', 'Recommendation Letters (2)', 'English Test', 'GMAT Scores'],
    interviewScore: 75,
    employerRecommendation: null
  }];
  const trackOptions = ['All Tracks', 'Elite', 'Partnership'];
  const statusOptions = ['All Statuses', 'under_review', 'final_review', 'pending_decision', 'approved', 'rejected'];
  const priorityOptions = ['All Priorities', 'Critical', 'High', 'Medium', 'Low'];
  const getStatusColor = status => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending_decision':
        return 'bg-yellow-100 text-yellow-800';
      case 'final_review':
        return 'bg-purple-100 text-purple-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusText = status => {
    switch (status) {
      case 'under_review':
        return 'Under Review';
      case 'final_review':
        return 'Final Review';
      case 'pending_decision':
        return 'Pending Decision';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };
  const getPriorityColor = priority => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-800';
      case 'High':
        return 'bg-orange-100 text-orange-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Filter applications
  const filteredApplications = applications.filter(application => {
    const matchesSearch = searchQuery === '' || application.studentName.toLowerCase().includes(searchQuery.toLowerCase()) || application.studentId.toLowerCase().includes(searchQuery.toLowerCase()) || application.program.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTrack = trackFilter === 'All Tracks' || application.track === trackFilter;
    const matchesStatus = statusFilter === 'All Statuses' || application.status === statusFilter;
    const matchesPriority = priorityFilter === 'All Priorities' || application.priority === priorityFilter;
    return matchesSearch && matchesTrack && matchesStatus && matchesPriority;
  });
  const openDetailModal = application => {
    setSelectedApplication(application);
    setIsDetailModalOpen(true);
  };
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedApplication(null);
  };
  return <Layout title="Scholarship Applications" persona="MoHESR Admin" data-id="element-2543">
      <div className="max-w-7xl mx-auto" data-id="element-2544">
        {/* Header with stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-2545">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2546">
            <div className="flex items-start justify-between" data-id="element-2547">
              <div data-id="element-2548">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2549">
                  Total Applications
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-2550">127</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2551">This academic year</p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-2552">
                <FileTextIcon className="w-6 h-6 text-primary-500" data-id="element-2553" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2554">
            <div className="flex items-start justify-between" data-id="element-2555">
              <div data-id="element-2556">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2557">
                  Pending Decision
                </h3>
                <div className="text-3xl font-bold text-yellow-500" data-id="element-2558">8</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2559">
                  Require final approval
                </p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full" data-id="element-2560">
                <ClockIcon className="w-6 h-6 text-yellow-500" data-id="element-2561" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2562">
            <div className="flex items-start justify-between" data-id="element-2563">
              <div data-id="element-2564">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2565">Approved</h3>
                <div className="text-3xl font-bold text-green-500" data-id="element-2566">45</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2567">
                  35% acceptance rate
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-full" data-id="element-2568">
                <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-2569" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-2570">
            <div className="flex items-start justify-between" data-id="element-2571">
              <div data-id="element-2572">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-2573">
                  Critical Priority
                </h3>
                <div className="text-3xl font-bold text-red-500" data-id="element-2574">3</div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2575">
                  Need immediate attention
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-full" data-id="element-2576">
                <AlertCircleIcon className="w-6 h-6 text-red-500" data-id="element-2577" />
              </div>
            </div>
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-2578">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4" data-id="element-2579">
            <div className="relative flex-grow max-w-md" data-id="element-2580">
              <input type="text" placeholder="Search applications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-2581" />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-2582" />
            </div>
            <button className="px-4 py-2 bg-primary-500 text-white rounded-md font-medium hover:bg-primary-600 transition-colors flex items-center" data-id="element-2583">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-2584" />
              Export Report
            </button>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-2585">
            <div className="relative" data-id="element-2586">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={trackFilter} onChange={e => setTrackFilter(e.target.value)} data-id="element-2587">
                {trackOptions.map(track => <option key={track} value={track} data-id="element-2588">
                    {track}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-2589" />
            </div>
            <div className="relative" data-id="element-2590">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-2591">
                {statusOptions.map(status => <option key={status} value={status} data-id="element-2592">
                    {status === 'All Statuses' ? 'All Statuses' : getStatusText(status)}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-2593" />
            </div>
            <div className="relative" data-id="element-2594">
              <select className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none pr-8" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} data-id="element-2595">
                {priorityOptions.map(priority => <option key={priority} value={priority} data-id="element-2596">
                    {priority}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-2 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-2597" />
            </div>
          </div>
        </div>
        {/* Applications Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2598">
          <div className="overflow-x-auto" data-id="element-2599">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2600">
              <thead className="bg-gray-50" data-id="element-2601">
                <tr data-id="element-2602">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2603">
                    Student
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2604">
                    Track
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2605">
                    Program
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2606">
                    Priority
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2607">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2608">
                    Recommendation
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2609">
                    Submitted
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2610">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2611">
                {filteredApplications.map(application => <tr key={application.id} className="hover:bg-gray-50" data-id="element-2612">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2613">
                      <div className="flex items-center" data-id="element-2614">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-2615">
                          <UserIcon className="w-5 h-5" data-id="element-2616" />
                        </div>
                        <div className="ml-4" data-id="element-2617">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2618">
                            {application.studentName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-2619">
                            {application.studentId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2620">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${application.track === 'Elite' ? 'bg-primary-100 text-primary-800' : 'bg-secondary-100 text-secondary-800'}`} data-id="element-2621">
                        {application.track}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2622">
                      <div className="text-sm text-gray-900" data-id="element-2623">
                        {application.program}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-2624">
                        {application.university}
                      </div>
                      {application.employer && <div className="text-sm text-gray-500" data-id="element-2625">
                          via {application.employer}
                        </div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2626">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(application.priority)}`} data-id="element-2627">
                        {application.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2628">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(application.status)}`} data-id="element-2629">
                        {getStatusText(application.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2630">
                      <div className="text-sm text-gray-900" data-id="element-2631">
                        {application.recommendation}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2632">
                      {formatDate(application.submissionDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-2633">
                      <div className="flex space-x-2" data-id="element-2634">
                        <button className="text-primary-500 hover:text-primary-700" title="View Details" onClick={() => openDetailModal(application)} data-id="element-2635">
                          <EyeIcon className="w-4 h-4" data-id="element-2636" />
                        </button>
                        {(application.status === 'pending_decision' || application.status === 'final_review') && <>
                            <button className="text-green-500 hover:text-green-700" title="Approve" data-id="element-2637">
                              <CheckCircleIcon className="w-4 h-4" data-id="element-2638" />
                            </button>
                            <button className="text-red-500 hover:text-red-700" title="Reject" data-id="element-2639">
                              <XCircleIcon className="w-4 h-4" data-id="element-2640" />
                            </button>
                          </>}
                        <button className="text-blue-500 hover:text-blue-700" title="Message Student" data-id="element-2641">
                          <MessageSquareIcon className="w-4 h-4" data-id="element-2642" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        {/* Detail Modal */}
        {isDetailModalOpen && selectedApplication && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-2643">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-2644">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailModal} data-id="element-2645"></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-2646">
                &#8203;
              </span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-2647">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-2648">
                  <div className="flex justify-between items-start mb-6" data-id="element-2649">
                    <div data-id="element-2650">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-2651">
                        {selectedApplication.studentName} - Application Review
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-2652">
                        {selectedApplication.program}
                      </p>
                    </div>
                    <button onClick={closeDetailModal} className="text-gray-400 hover:text-gray-600" data-id="element-2653">
                      <XCircleIcon className="w-6 h-6" data-id="element-2654" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2655">
                    {/* Student Information */}
                    <div data-id="element-2656">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-2657">
                        Student Information
                      </h4>
                      <div className="space-y-3" data-id="element-2658">
                        <div data-id="element-2659">
                          <span className="text-sm text-gray-500" data-id="element-2660">
                            Student ID:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2661">
                            {selectedApplication.studentId}
                          </p>
                        </div>
                        <div data-id="element-2662">
                          <span className="text-sm text-gray-500" data-id="element-2663">GPA:</span>
                          <p className="text-sm text-gray-900" data-id="element-2664">
                            {selectedApplication.gpa}
                          </p>
                        </div>
                        <div data-id="element-2665">
                          <span className="text-sm text-gray-500" data-id="element-2666">
                            English Score:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2667">
                            {selectedApplication.englishScore}
                          </p>
                        </div>
                        {selectedApplication.additionalTests && <div data-id="element-2668">
                            <span className="text-sm text-gray-500" data-id="element-2669">
                              Additional Tests:
                            </span>
                            <p className="text-sm text-gray-900" data-id="element-2670">
                              {selectedApplication.additionalTests}
                            </p>
                          </div>}
                        <div data-id="element-2671">
                          <span className="text-sm text-gray-500" data-id="element-2672">
                            Work Experience:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2673">
                            {selectedApplication.workExperience}
                          </p>
                        </div>
                        <div data-id="element-2674">
                          <span className="text-sm text-gray-500" data-id="element-2675">
                            Interview Score:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2676">
                            {selectedApplication.interviewScore}/100
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Program Information */}
                    <div data-id="element-2677">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-2678">
                        Program Information
                      </h4>
                      <div className="space-y-3" data-id="element-2679">
                        <div data-id="element-2680">
                          <span className="text-sm text-gray-500" data-id="element-2681">Track:</span>
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedApplication.track === 'Elite' ? 'bg-primary-100 text-primary-800' : 'bg-secondary-100 text-secondary-800'}`} data-id="element-2682">
                            {selectedApplication.track}
                          </span>
                        </div>
                        <div data-id="element-2683">
                          <span className="text-sm text-gray-500" data-id="element-2684">
                            University:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2685">
                            {selectedApplication.university}
                          </p>
                        </div>
                        {selectedApplication.employer && <div data-id="element-2686">
                            <span className="text-sm text-gray-500" data-id="element-2687">
                              Employer:
                            </span>
                            <p className="text-sm text-gray-900" data-id="element-2688">
                              {selectedApplication.employer}
                            </p>
                          </div>}
                        {selectedApplication.employerRecommendation && <div data-id="element-2689">
                            <span className="text-sm text-gray-500" data-id="element-2690">
                              Employer Recommendation:
                            </span>
                            <p className="text-sm text-gray-900" data-id="element-2691">
                              {selectedApplication.employerRecommendation}
                            </p>
                          </div>}
                        <div data-id="element-2692">
                          <span className="text-sm text-gray-500" data-id="element-2693">
                            Priority:
                          </span>
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(selectedApplication.priority)}`} data-id="element-2694">
                            {selectedApplication.priority}
                          </span>
                        </div>
                        <div data-id="element-2695">
                          <span className="text-sm text-gray-500" data-id="element-2696">
                            Current Status:
                          </span>
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedApplication.status)}`} data-id="element-2697">
                            {getStatusText(selectedApplication.status)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Documents */}
                    <div data-id="element-2698">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-2699">
                        Submitted Documents
                      </h4>
                      <div className="space-y-2" data-id="element-2700">
                        {selectedApplication.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded" data-id="element-2701">
                            <span className="text-sm text-gray-700" data-id="element-2702">{doc}</span>
                            <button className="text-primary-500 hover:text-primary-700 text-sm" data-id="element-2703">
                              View
                            </button>
                          </div>)}
                      </div>
                    </div>
                    {/* Review Information */}
                    <div data-id="element-2704">
                      <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-2705">
                        Review Information
                      </h4>
                      <div className="space-y-3" data-id="element-2706">
                        <div data-id="element-2707">
                          <span className="text-sm text-gray-500" data-id="element-2708">
                            Recommendation:
                          </span>
                          <p className="text-sm text-gray-900 font-medium" data-id="element-2709">
                            {selectedApplication.recommendation}
                          </p>
                        </div>
                        <div data-id="element-2710">
                          <span className="text-sm text-gray-500" data-id="element-2711">
                            Review Notes:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2712">
                            {selectedApplication.reviewNotes}
                          </p>
                        </div>
                        <div data-id="element-2713">
                          <span className="text-sm text-gray-500" data-id="element-2714">
                            Submission Date:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2715">
                            {formatDate(selectedApplication.submissionDate)}
                          </p>
                        </div>
                        <div data-id="element-2716">
                          <span className="text-sm text-gray-500" data-id="element-2717">
                            Last Updated:
                          </span>
                          <p className="text-sm text-gray-900" data-id="element-2718">
                            {formatDate(selectedApplication.lastUpdated)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Modal Actions */}
                {(selectedApplication.status === 'pending_decision' || selectedApplication.status === 'final_review') && <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-2719">
                    <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-2720">
                      Approve Application
                    </button>
                    <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-2721">
                      Reject Application
                    </button>
                    <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm" onClick={closeDetailModal} data-id="element-2722">
                      Close
                    </button>
                  </div>}
              </div>
            </div>
          </div>}
      </div>
    </Layout>;
};
export default MoHESRApplications;